import React, { useEffect, useState } from 'react';
import {
  PublicClientApplication,
  EventType,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from '../config/auth.config';

/**
 * Singleton MSAL instance used across the application.
 */
const msalInstance = new PublicClientApplication(msalConfig);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication provider that wraps the app with MSAL context.
 * Handles initialization and sets the active account on login success.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();

        // Handle redirect response on page load
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          msalInstance.setActiveAccount(response.account);
        }

        // If no active account, set the first available account
        if (!msalInstance.getActiveAccount()) {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
          }
        }

        // Listen for login success events to set active account
        msalInstance.addEventCallback((event) => {
          if (
            event.eventType === EventType.LOGIN_SUCCESS &&
            event.payload
          ) {
            const payload = event.payload as AuthenticationResult;
            msalInstance.setActiveAccount(payload.account);
          }
        });

        setIsInitialized(true);
      } catch (error) {
        console.error('[Auth] MSAL initialization failed:', error);
        setIsInitialized(true); // Still render the app so the login page can show
      }
    };

    initializeMsal();
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#EDE6DE]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-3 border-[#C08B5C] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-[#6B7280] font-medium tracking-wide">
            Initializing...
          </p>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      {children}
    </MsalProvider>
  );
};

export { msalInstance };
